import { useState, useRef, useMemo } from 'react'

import {
  Box,
  Popover,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  MenuItem,
  Select,
} from '@mui/material'
import { CalendarDays, ChevronLeft, ChevronRight, X, Clock } from 'lucide-react'

import FieldLabel from './FieldLabel'

const DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]

const toDate = (value) => {
  if (!value) return null
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

const formatDisplay = (date) => {
  if (!date) return ''
  const dd = String(date.getDate()).padStart(2, '0')
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mi = String(date.getMinutes()).padStart(2, '0')
  return `${dd}/${mm}/${date.getFullYear()} ${hh}:${mi}`
}

const formatISO = (date) => {
  if (!date) return ''
  const mm = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mi = String(date.getMinutes()).padStart(2, '0')
  return `${date.getFullYear()}-${mm}-${dd}T${hh}:${mi}`
}

const isSameDay = (a, b) =>
  a &&
  b &&
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate()

const HOURS = Array.from({ length: 24 }, (_, i) => i)
const MINUTES = Array.from({ length: 60 }, (_, i) => i)

function CustomDateTimePicker({
  label,
  value,
  onChange,
  size = 'small',
  required = false,
  disabled = false,
  placeholder = 'DD/MM/YYYY HH:mm',
  fullWidth = true,
  minDate,
  maxDate,
  minuteStep = 1,
  sx,
  inputSx,
  className,
  clearable = true,
  ...rest
}) {
  const inputRef = useRef(null)
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const selectedDate = useMemo(() => toDate(value), [value])
  const [viewDate, setViewDate] = useState(() => selectedDate || new Date())
  const [hour, setHour] = useState(() => (selectedDate ? selectedDate.getHours() : 0))
  const [minute, setMinute] = useState(() => (selectedDate ? selectedDate.getMinutes() : 0))

  const minD = useMemo(() => toDate(minDate), [minDate])
  const maxD = useMemo(() => toDate(maxDate), [maxDate])

  const handleOpen = (e) => {
    if (disabled) return
    const parsed = toDate(value) || new Date()
    setHour(parsed.getHours())
    setMinute(parsed.getMinutes())
    setViewDate(new Date(parsed.getFullYear(), parsed.getMonth(), 1))
    setAnchorEl(e.currentTarget)
  }
  const handleClose = () => setAnchorEl(null)

  const handleSelectDay = (day) => {
    if (disabled) return
    const next = new Date(viewDate.getFullYear(), viewDate.getMonth(), day, hour, minute)
    if (minD && next < new Date(minD.getFullYear(), minD.getMonth(), minD.getDate(), 0, 0)) return
    if (maxD && next > new Date(maxD.getFullYear(), maxD.getMonth(), maxD.getDate(), 23, 59)) return
    onChange?.({ target: { value: formatISO(next), name: rest.name } })
    setAnchorEl(null)
  }

  const handleTimeChange = (kind, val) => {
    const h = kind === 'hour' ? Number(val) : hour
    const m = kind === 'minute' ? Number(val) : minute
    setHour(h)
    setMinute(m)
    if (selectedDate) {
      const next = new Date(selectedDate)
      next.setHours(h)
      next.setMinutes(m)
      onChange?.({ target: { value: formatISO(next), name: rest.name } })
    }
  }

  const handleClear = (e) => {
    e.stopPropagation()
    onChange?.({ target: { value: '', name: rest.name } })
  }

  const prevMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1))
  const nextMonth = () => setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1))

  const today = new Date()
  const firstDay = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay()
  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate()

  const cells = useMemo(() => {
    const arr = []
    for (let i = 0; i < firstDay; i++) arr.push(null)
    for (let d = 1; d <= daysInMonth; d++) arr.push(d)
    while (arr.length % 7 !== 0) arr.push(null)
    return arr
  }, [firstDay, daysInMonth])

  const isDisabled = (day) => {
    const d = new Date(viewDate.getFullYear(), viewDate.getMonth(), day)
    if (minD && d < new Date(minD.getFullYear(), minD.getMonth(), minD.getDate())) return true
    if (maxD && d > new Date(maxD.getFullYear(), maxD.getMonth(), maxD.getDate())) return true
    return false
  }

  const minuteOptions = MINUTES.filter((m) => m % minuteStep === 0)

  return (
    <div className="flex flex-col" style={{ gap: '2px' }}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <TextField
        ref={inputRef}
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        required={required}
        label={undefined}
        value={formatDisplay(selectedDate)}
        placeholder={placeholder}
        onClick={handleOpen}
        onChange={() => {}}
        className={className}
        InputProps={{
          readOnly: true,
          sx: {
            cursor: disabled ? 'default' : 'pointer',
            ...inputSx,
          },
          endAdornment: (
            <InputAdornment position="end" sx={{ gap: 0.25 }}>
              {clearable && selectedDate && !disabled && (
                <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                  <X size={13} color="var(--input-placeholder)" />
                </IconButton>
              )}
              <CalendarDays size={15} color="var(--input-placeholder)" />
            </InputAdornment>
          ),
        }}
        sx={sx}
        inputProps={{ ...rest, sx: { cursor: disabled ? 'default' : 'pointer' } }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: 2,
              backgroundColor: 'var(--modal-bg-color)',
              color: 'var(--input-text)',
              border: '1px solid var(--card-border)',
              p: 1.5,
              minWidth: 280,
            },
          },
        }}
      >
        <Box
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}
        >
          <IconButton size="small" onClick={prevMonth} sx={{ color: 'var(--input-text)' }}>
            <ChevronLeft size={16} />
          </IconButton>
          <Typography variant="body2" fontWeight={600} sx={{ color: 'var(--modal-header-color)' }}>
            {MONTHS[viewDate.getMonth()]} {viewDate.getFullYear()}
          </Typography>
          <IconButton size="small" onClick={nextMonth} sx={{ color: 'var(--input-text)' }}>
            <ChevronRight size={16} />
          </IconButton>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
          {DAYS.map((d) => (
            <Box
              key={d}
              sx={{
                textAlign: 'center',
                fontSize: '0.7rem',
                fontWeight: 600,
                color: 'var(--input-placeholder)',
                py: 0.5,
              }}
            >
              {d}
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.25, mb: 1.5 }}>
          {cells.map((day, i) => {
            if (day === null) {
              return <Box key={`empty-${i}`} />
            }
            const cellDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day)
            const isSelected = isSameDay(cellDate, selectedDate)
            const isToday = isSameDay(cellDate, today)
            const disabled = isDisabled(day)
            return (
              <Box
                key={day}
                onClick={() => !disabled && handleSelectDay(day)}
                sx={{
                  textAlign: 'center',
                  fontSize: '0.8125rem',
                  py: 0.75,
                  borderRadius: 1,
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  color: disabled
                    ? 'var(--input-placeholder)'
                    : isSelected
                      ? '#fff'
                      : 'var(--input-text)',
                  backgroundColor: isSelected ? '#3B82F6' : 'transparent',
                  border: isToday && !isSelected ? '1px solid #3B82F6' : '1px solid transparent',
                  opacity: disabled ? 0.4 : 1,
                  '&:hover': disabled
                    ? {}
                    : {
                        backgroundColor: isSelected
                          ? '#3B82F6'
                          : 'var(--checkbox-hover-bg, rgba(59,130,246,0.12))',
                      },
                  transition: 'background-color 0.15s',
                }}
              >
                {day}
              </Box>
            )
          })}
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pt: 1,
            borderTop: '1px solid var(--card-border)',
          }}
        >
          <Clock size={14} color="var(--input-placeholder)" />
          <Select
            size="small"
            value={hour}
            onChange={(e) => handleTimeChange('hour', e.target.value)}
            sx={{
              fontSize: '0.8125rem',
              minWidth: 70,
              color: 'var(--input-text)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--card-border)' },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'var(--modal-bg-color)',
                  color: 'var(--input-text)',
                  maxHeight: 220,
                },
              },
            }}
          >
            {HOURS.map((h) => (
              <MenuItem key={h} value={h} sx={{ fontSize: '0.8125rem' }}>
                {String(h).padStart(2, '0')}
              </MenuItem>
            ))}
          </Select>
          <Typography sx={{ color: 'var(--input-text)' }}>:</Typography>
          <Select
            size="small"
            value={minute}
            onChange={(e) => handleTimeChange('minute', e.target.value)}
            sx={{
              fontSize: '0.8125rem',
              minWidth: 70,
              color: 'var(--input-text)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--card-border)' },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  backgroundColor: 'var(--modal-bg-color)',
                  color: 'var(--input-text)',
                  maxHeight: 220,
                },
              },
            }}
          >
            {minuteOptions.map((m) => (
              <MenuItem key={m} value={m} sx={{ fontSize: '0.8125rem' }}>
                {String(m).padStart(2, '0')}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Popover>
    </div>
  )
}

export { CustomDateTimePicker }
export default CustomDateTimePicker
